import { useCallback, useContext } from 'react';
import { IEditorProps } from './Editor';
import { DataDesigner } from 'domains/designers/DataDesigner/DataDesigner';
import { startSessionForEditor } from '@decisions/server/DecisionsFramework/Design/Flow/Service/Edit/InputData/DataDesignerService';
import { PropertyEditorContext } from 'components/property-editors/contexts/PropertyEditorContext';
import { HasOpenState } from 'hooks/HasOpenState';
import { EditorLabel } from './EditorLabel';
import { EditorConfigButtons } from './EditorConfigButtons';
import { EditorConfigButton } from './EditorConfigButton';
import { DataDesignerSurface } from '@decisions/server/DecisionsFramework/Design/Flow/Service/Edit/InputData';
import { IListItemProps } from 'components/lists/ListItem';
import { IPropertyChild } from 'components/property-editors/utils/ProviderNodeUtils';
import { ListBox } from 'components/lists/ListBox';
import { removeDataDiagram } from '@decisions/server/DecisionsFramework/Design/Properties/PropertyEditorService';
import { getEditorConfigValue } from 'components/property-editors/utils/EditorConfigurationUtils';
import { AwaitableContext } from 'contexts/AwaitableContext';

export interface IDataDefinitionEditorProps extends IEditorProps {}

/**
 * Property Editor that opens a Data Designer.
 *
 * Copyright (c) 2019 Decisions ALl Rights Reserved
 */
export function DataDesignerEditor({ child, path }: IDataDefinitionEditorProps) {
  const context = useContext(PropertyEditorContext);
  const { addPromise, removePromise } = useContext(AwaitableContext);
  const { isOpen, setOpen, setClosed } = HasOpenState();

  const handleStartSession = useCallback(() => {
    return startSessionForEditor({
      mappingContext: context.mappingContext,
      editingContextId: context.editingContextId,
      path,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [context.editingContextId, context.mappingContext]);

  const handleRemove = useCallback(
    (index: number) => {
      const allDiagrams = (child.Value.OutputValue as DataDesignerSurface).Diagrams ?? [];
      const diagram = allDiagrams[index];
      const id = path.join(',');
      if (diagram != null) {
        addPromise(
          id,
          (async () => {
            await removeDataDiagram({
              editingContextId: context.editingContextId,
              path,
              diagramId: diagram.Id,
            });
            removePromise(id);
          })()
        );
      }
    },
    [addPromise, child.Value.OutputValue, context.editingContextId, path, removePromise]
  );

  return (
    <EditorLabel editor={child} path={path} doNotWrap className="dpc-data-designer-editor">
      <ListBox items={getListItems(child)} onRemove={allowToEdit(child) ? handleRemove : undefined} />
      <EditorConfigButtons>
        {allowToEdit(child) && <EditorConfigButton onClick={setOpen}>Show Editor</EditorConfigButton>}
      </EditorConfigButtons>
      {isOpen && (
        <DataDesigner
          currentView={'List View'}
          startSession={handleStartSession}
          onCreateTypeClick={$DP?.Designers?.showCreateDataTypeIntegrationDialog}
          hasIndent={true}
          mappingContext={context.mappingContext}
          onCreateTypeFromInputs={$DP?.Designers?.showCreateTypeFromInputsDialog}
          showSaveDialog={$DP?.Designers?.showDataDesignerSaveDialog}
          onClose={setClosed}
        />
      )}
    </EditorLabel>
  );
}

function getListItems(child: IPropertyChild): IListItemProps[] {
  const items = getEditorConfigValue<string[]>(child, 'ItemLabelsKey');

  return items.map((label, index) => ({
    index,
    label,
  }));
}

function allowToEdit(child: IPropertyChild): boolean {
  return !getEditorConfigValue(child, 'Readonly');
}
